import { Badge, type BadgeVariant } from '@cloudflare/kumo'
import {
  QUOTE_SLA_DAYS,
  type AdminQuoteView,
  type FactoryStatus,
  type QuoteFeeFrequency,
  type QuoteOfferEventKind,
  type QuoteSample,
  type QuoteRequestStatus,
  type QuoteSampleAvailability,
  type QuoteStatus,
  type QuoteStatusActor,
} from '@repo/shared'
import { fmtMoney } from './format'

export const STATUS_LABEL: Record<QuoteStatus, string> = {
  submitted: '견적 대기',
  quoted: '견적 보냄',
  accepted: '공장 선택됨',
  closed: '마감',
}
export const STATUS_BADGE: Record<QuoteStatus, BadgeVariant> = {
  submitted: 'warning',
  quoted: 'info',
  accepted: 'success',
  closed: 'outline',
}
/** 명세 상태 (packages/shared quote-status.ts). 상태 이력과 새 단계 버튼에 쓴다 */
export const REQUEST_STATUS_LABEL: Record<QuoteRequestStatus, string> = {
  draft: '작성 중',
  submitted: '요청됨',
  reviewing: '공장 확인 중',
  needs_information: '추가 정보 요청',
  offered: '제안 도착',
  closed: '마감',
  cancelled: '고객 취소',
}
export const STATUS_ACTOR_LABEL: Record<QuoteStatusActor, string> = {
  customer: '고객',
  admin: '관리자',
  factory: '공장',
  system: '자동',
}
export const STORAGE_LABEL = { ambient: '상온', refrigerated: '냉장', frozen: '냉동' } as const

/** 공장 목록 상태 (로드맵 1위). 관리자 화면 전용이라 한국어만 둔다 */
export const FACTORY_STATUS_LABEL: Record<FactoryStatus, string> = {
  lead: '영업 리스트',
  replied: '회신',
  negotiating: '협의 중',
  partner: '파트너',
  inactive: '중단',
  do_not_contact: '연락 금지',
}

export const FREQ_LABEL: Record<QuoteFeeFrequency, string> = {
  one_time: '1회',
  per_batch: '배치당',
  monthly: '월',
  annual: '연',
}

/** 공장 샘플 조건 (2026-10-25) */
export const SAMPLE_LABEL: Record<QuoteSampleAvailability, string> = {
  free: '무료 샘플',
  paid: '유료 샘플',
  none: '샘플 없음',
}

/** "유료 샘플 30,000원 · 7일" 처럼 한 줄로. null = 아직 모름 */
export function sampleText(sample: QuoteSample | null | undefined, currency: string): string {
  if (!sample) return '미입력'
  const parts = [SAMPLE_LABEL[sample.availability]]
  if (sample.availability === 'paid' && sample.price !== null)
    parts.push(fmtMoney(sample.price, currency))
  if (sample.availability !== 'none' && sample.leadTimeDays !== null)
    parts.push(`${sample.leadTimeDays}일`)
  return parts.join(' · ')
}

export const EVENT_LABEL: Record<QuoteOfferEventKind, string> = {
  added: '입력',
  updated: '수정',
  removed: '삭제',
}
export const EVENT_BADGE: Record<QuoteOfferEventKind, BadgeVariant> = {
  added: 'success',
  updated: 'info',
  removed: 'error',
}

/** 접수 후 경과 — 견적 대기 중이면 SLA 기준으로 색을 바꾼다 */
export function AgeBadge({ q }: { q: AdminQuoteView }) {
  const text = q.ageDays < 1 ? '오늘' : `${Math.floor(q.ageDays)}일 전`
  if (q.status !== 'submitted') return <span className="text-kumo-subtle text-xs">{text}</span>
  if (q.overdue) return <Badge variant="error">지연 · {text}</Badge>
  if (q.ageDays >= QUOTE_SLA_DAYS - 1) return <Badge variant="warning">임박 · {text}</Badge>
  return <span className="text-kumo-subtle text-xs">{text}</span>
}
